/**
 * People Card
 *
 * Displays team members, founders, advisors extracted from the document.
 * Shows role, relationship badge, background, and contact info.
 */

import React from 'react';
import { View, Text } from 'react-native';
import { Users, Crown, Briefcase, GraduationCap, Linkedin, Mail } from 'lucide-react-native';
import Animated, { FadeIn, FadeInDown } from 'react-native-reanimated';
import type { RichExtractionPerson } from '@/lib/supabase/edge-functions';
import { useThemeColors } from '@/lib/useThemeColors';

const RELATIONSHIP_CONFIG: Record<string, { color: string; bg: string; icon: typeof Crown }> = {
  founder: { color: '#D97706', bg: '#FEF3C7', icon: Crown },
  'co-founder': { color: '#D97706', bg: '#FEF3C7', icon: Crown },
  cto: { color: '#7C3AED', bg: '#EDE9FE', icon: Briefcase },
  coo: { color: '#7C3AED', bg: '#EDE9FE', icon: Briefcase },
  cfo: { color: '#7C3AED', bg: '#EDE9FE', icon: Briefcase },
  advisor: { color: '#2563EB', bg: '#DBEAFE', icon: GraduationCap },
  'board-member': { color: '#2563EB', bg: '#DBEAFE', icon: GraduationCap },
  investor: { color: '#059669', bg: '#D1FAE5', icon: Briefcase },
  'team-member': { color: '#6B7280', bg: '#F3F4F6', icon: Users },
};

interface PeopleCardProps {
  people: RichExtractionPerson[];
}

export function PeopleCard({ people }: PeopleCardProps) {
  const colors = useThemeColors();

  // Sort: founders first, then by name
  const sorted = [...people].sort((a, b) => {
    const order = ['founder', 'co-founder', 'cto', 'coo', 'cfo', 'advisor', 'board-member', 'investor', 'team-member'];
    return (order.indexOf(a.relationship) - order.indexOf(b.relationship)) || a.name.localeCompare(b.name);
  });

  return (
    <Animated.View entering={FadeIn.delay(200).duration(400)}>
      <View
        className="rounded-2xl overflow-hidden"
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        {/* Header */}
        <View className="p-4 pb-3">
          <View className="flex-row items-center gap-2.5">
            <View
              className="w-8 h-8 rounded-lg items-center justify-center"
              style={{ backgroundColor: '#FDF2F8' }}
            >
              <Users size={16} color="#EC4899" />
            </View>
            <View className="flex-1">
              <Text style={{ color: colors.text.primary }} className="text-sm font-bold">
                People
              </Text>
              <Text style={{ color: colors.text.tertiary }} className="text-xs">
                {people.length} identified from deck
              </Text>
            </View>
          </View>
        </View>

        {/* People list */}
        <View style={{ borderTopWidth: 1, borderTopColor: colors.border.light }}>
          {sorted.map((person, i) => {
            const config = RELATIONSHIP_CONFIG[person.relationship] || RELATIONSHIP_CONFIG['team-member'];
            const RelIcon = config.icon;

            return (
              <Animated.View
                key={`${person.name}-${i}`}
                entering={FadeInDown.delay(i * 40).duration(250)}
                className="px-4 py-3"
                style={i < sorted.length - 1 ? { borderBottomWidth: 0.5, borderBottomColor: colors.border.light } : undefined}
              >
                <View className="flex-row items-start gap-3">
                  {/* Avatar circle with initials */}
                  <View
                    className="w-10 h-10 rounded-full items-center justify-center"
                    style={{ backgroundColor: config.bg }}
                  >
                    <Text style={{ color: config.color }} className="text-sm font-bold">
                      {person.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </Text>
                  </View>

                  <View className="flex-1">
                    {/* Name + relationship badge */}
                    <View className="flex-row items-center gap-2 mb-0.5">
                      <Text style={{ color: colors.text.primary }} className="text-sm font-semibold">
                        {person.name}
                      </Text>
                      <View
                        className="flex-row items-center gap-1 px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: config.bg }}
                      >
                        <RelIcon size={10} color={config.color} />
                        <Text style={{ color: config.color }} className="text-xs font-medium">
                          {person.relationship}
                        </Text>
                      </View>
                    </View>

                    {/* Role */}
                    {person.role ? (
                      <Text style={{ color: colors.text.secondary }} className="text-xs">
                        {person.role}
                      </Text>
                    ) : null}

                    {/* Background */}
                    {person.background ? (
                      <Text style={{ color: colors.text.tertiary }} className="text-xs italic mt-0.5">
                        {person.background}
                      </Text>
                    ) : null}

                    {/* Contact row */}
                    {(person.linkedIn || person.email) && (
                      <View className="flex-row items-center gap-3 mt-1.5">
                        {person.linkedIn && (
                          <View className="flex-row items-center gap-1">
                            <Linkedin size={10} color={colors.primary[600]} />
                            <Text style={{ color: colors.primary[600] }} className="text-xs" numberOfLines={1}>
                              LinkedIn
                            </Text>
                          </View>
                        )}
                        {person.email && (
                          <View className="flex-row items-center gap-1">
                            <Mail size={10} color={colors.primary[600]} />
                            <Text style={{ color: colors.primary[600] }} className="text-xs" numberOfLines={1}>
                              {person.email}
                            </Text>
                          </View>
                        )}
                      </View>
                    )}
                  </View>
                </View>
              </Animated.View>
            );
          })}
        </View>
      </View>
    </Animated.View>
  );
}
